import { addOrReplaceSsoLinkParams } from '../components/redirect-if-page-not-available/hooks/add-or-replace-sso-link-params';
import useLocale from '../locale/use-locale/use-locale';
import type { LoginMethod } from '../selectors/interfaces';
import { useGetLoginConfig } from './useGetLoginConfig';
import { usePostLoginRedirectDestination } from './usePostLoginRedirectDestination';

export const useGetSsoLoginLink = () => {
  const { locale, userHasPickedLanguage } = useLocale();
  const { getLoginMethodConfig } = useGetLoginConfig();

  const { getPostLoginRedirectDestination } = usePostLoginRedirectDestination();

  // We use userPickedLanguage to know whether to overwrite the user's existing
  // language preference when logging in.
  // E.g. if the user selects Spanish while unauthenticated, and then logs in,
  // we want to tell the backend to save their language as Spanish. But if the
  // user logs in without switching away from the default language, we don't
  // want to overwrite their language on the backend with the default one that
  // they didn't pick.
  const shouldSaveUserLanguagePreference = userHasPickedLanguage;

  const getSsoLinkWithParams = (
    rawSsoLink: string,
    shouldRedirectToCurrentPageAfterLogin = true,
  ): string => {
    const postLoginRedirectDestination = getPostLoginRedirectDestination(
      true,
      shouldRedirectToCurrentPageAfterLogin,
    );
    return addOrReplaceSsoLinkParams({
      ssoUrl: rawSsoLink,
      postLoginDestinationNotUrlEncoded: postLoginRedirectDestination,
      selectedLanguage: locale,
      shouldSaveAsUsersPermanentLanguage: shouldSaveUserLanguagePreference,
    });
  };

  const getSsoLinkForLoginMethod = (
    loginMethod: LoginMethod,
    shouldRedirectToCurrentPageAfterLogin = true,
    logErrors = true,
  ): string | undefined => {
    const loginMethodConfig = getLoginMethodConfig(loginMethod, logErrors);
    if (!loginMethodConfig) {
      return undefined;
    }

    if (!loginMethodConfig?.oauth_login_url) {
      if (logErrors) {
        console.error(
          `Login method "${loginMethod}" doesn't provide login url.`,
        );
      }
      return undefined;
    }

    return getSsoLinkWithParams(
      loginMethodConfig.oauth_login_url,
      shouldRedirectToCurrentPageAfterLogin,
    );
  };

  return { getSsoLinkWithParams, getSsoLinkForLoginMethod };
};
